import axios from "axios";
/**
 * Create Axios Instances and Interceptors
 *
 * Defaults to production. For local backend development, set in .env:
 * REACT_APP_API_URL=http://localhost:3001
 *
 * REACT_APP_API_URL may be set with or without a trailing /api segment.
 * Route constants in this app always start with /api/...
 *
 * Restart `npm start` after changing .env (Create React App reads env at startup).
 */

const PRODUCTION_API_URL = "https://api.portal.madukauniversity.edu.ng";
const DEVELOPMENT_API_URL = "http://localhost:3001";

function normalizeBaseUrl(url) {
  if (!url || typeof url !== "string") {
    return url;
  }

  return url.trim().replace(/\/+$/, "");
}

/**
 * Strip a trailing /api segment so paths like /api/faculty do not become /api/api/faculty.
 */
function normalizeApiBaseUrl(url) {
  const normalized = normalizeBaseUrl(url);

  if (!normalized) {
    return normalized;
  }

  return normalized.replace(/\/api$/i, "");
}

function resolveBaseUrl() {
  const fromEnv = normalizeBaseUrl(
    process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL || ""
  );

  if (fromEnv) {
    return fromEnv;
  }

  if (process.env.NODE_ENV === "development") {
    return DEVELOPMENT_API_URL;
  }

  return PRODUCTION_API_URL;
}

export const baseUrl = normalizeApiBaseUrl(resolveBaseUrl());

export const API_ROUTES = {
  faculty: "/api/faculty",
  department: "/api/department",
  currentSession: "/api/session/current-session",
};

export function apiUrl(path = "") {
  if (typeof path === "string" && (path.startsWith("http://") || path.startsWith("https://"))) {
    return path;
  }

  let normalizedPath = path.startsWith("/") ? path : `/${path}`;
  normalizedPath = normalizedPath.replace(/^\/api\/api(\/|$)/i, "/api$1");

  return `${baseUrl}${normalizedPath}`;
}

if (process.env.NODE_ENV === "development") {
  console.info("[mu-admissions] API base URL:", baseUrl);
}

/**
 * Read the auth token from localStorage.
 *
 * Login pages store the token with JSON.stringify, while older code stored it
 * raw. Supporting both keeps the Authorization header valid either way.
 */
export function getStoredToken() {
  const stored = localStorage.getItem("_token");

  if (!stored) {
    return "";
  }

  try {
    const parsed = JSON.parse(stored);

    return typeof parsed === "string" ? parsed : "";
  } catch (error) {
    return stored;
  }
}

let api = axios.create({ baseURL: baseUrl });

api.interceptors.response.use(
  function (response) {
    return response.data;
  },
  function (error) {
    const status = error.response?.status;

    if (!error.response) {
      return Promise.reject(error);
    }

    /*
     * A 401/419 is normal while an applicant is filling or paying for a public
     * application (for example, the protected Paystack verification endpoint).
     * Hard-reloading the SPA there wiped the form and made submission/payment
     * look like it was looping. Only bounce an authenticated dashboard user
     * whose session has expired.
     */
    const hasSession = !!localStorage.getItem("_token");
    const isDashboardRoute =
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/dashboard");

    if (hasSession && isDashboardRoute && (status === 401 || status === 419)) {
      window.location.replace("/");
    }

    if (error.response.data) {
      return Promise.reject({
        ...error.response.data,
        statusCode: status,
      });
    } else if (error.request) {
      return Promise.reject({
        ...error.request,
        statusCode: status,
      });
    } else {
      return Promise.reject(error);
    }
  }
);

let Setup = () => {
  api.interceptors.request.use(
    function (config) {
      const token = getStoredToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    function (err) {
      return Promise.reject(err);
    }
  );
};

let token = localStorage.getItem("_token") || "";
const headers = {
  "Content-Type": "application/json",
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
  Authorization: `Bearer ${token}`,
};

const fetchApi = (url, method, data = "") => {
  const FULL_URL = url.startsWith("http") ? url : apiUrl(url);
  let config = {};
  config.method = method.toUpperCase();
  config.headers = headers;
  if (data) config.body = JSON.stringify(data);
  return new Promise((resolve, reject) => {
    fetch(FULL_URL, config)
      .then((response) => response.json())
      .then((dat) => resolve(dat))
      .catch((error) => reject(error));
  });
};

const getCurrentSession = (url) => {
  const sessionUrl = url?.startsWith("http") ? url : apiUrl(url || API_ROUTES.currentSession);

  return new Promise(async (resolve, reject) => {
    try {
      const currentSession = await fetch(sessionUrl, { cache: "no-cache" });
      const session = await currentSession.json();

      resolve(session);
    } catch (e) {
      console.error("Error fetching current session<>", e);

      return reject(e.message);
    }
  });
};

export { api, Setup, fetchApi, getCurrentSession };
